import { useState, useEffect, useCallback } from 'react';

export interface UseSearchDebounceOptions {
  /** Delay when search is empty (default: 1000ms) */
  emptyDelay?: number;
  /** Delay when search has value (default: 500ms) */
  valueDelay?: number;
  /** Callback when debounced query changes */
  onQueryChange?: (query: string) => void;
}

export interface UseSearchDebounceReturn {
  /** Current input value (controlled) */
  searchValue: string;
  /** Set search input value */
  setSearchValue: (value: string) => void;
  /** Debounced query value (use for API calls) */
  debouncedQuery: string;
  /** Clear search and reset */
  clearSearch: () => void;
  /** Handle form submit (immediate search) */
  handleSubmit: (e: React.FormEvent) => void;
}

/**
 * Hook for debounced search input with configurable delays.
 *
 * Consolidates the common pattern found across settings pages:
 * - Immediate search on form submit
 * - Debounced search on input change
 * - Different delays for empty vs. non-empty values
 *
 * @example
 * ```tsx
 * const { searchValue, setSearchValue, debouncedQuery, handleSubmit } = useSearchDebounce();
 *
 * // Use debouncedQuery for API calls
 * const { data } = useQuery(['items', debouncedQuery], () => fetchItems(debouncedQuery));
 *
 * // Use searchValue and setSearchValue for controlled input
 * <form onSubmit={handleSubmit}>
 *   <SearchInput value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
 * </form>
 * ```
 */
export function useSearchDebounce(
  options: UseSearchDebounceOptions = {},
): UseSearchDebounceReturn {
  const { emptyDelay = 1000, valueDelay = 500, onQueryChange } = options;

  const [searchValue, setSearchValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const delay = searchValue === '' ? emptyDelay : valueDelay;

    const timer = setTimeout(() => {
      if (debouncedQuery !== searchValue) {
        setDebouncedQuery(searchValue);
        onQueryChange?.(searchValue);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [searchValue, debouncedQuery, emptyDelay, valueDelay, onQueryChange]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setDebouncedQuery('');
    onQueryChange?.('');
  }, [onQueryChange]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // Immediate search on form submit
      setDebouncedQuery(searchValue);
      onQueryChange?.(searchValue);
    },
    [searchValue, onQueryChange],
  );

  return {
    searchValue,
    setSearchValue,
    debouncedQuery,
    clearSearch,
    handleSubmit,
  };
}
